import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AppSettingsSubscriptionSteps,
  butcherSubscriptionsStatus,
  errorMessage,
  OrderProcessingMasterData,
} from '@helpers/Constants';
import { useNavigate } from 'react-router-dom';
import { ButcherSubscriptionDetails } from '../SubscriptionStatus/ButcherSubscriptionDetails';
import { useEffect, useState } from 'react';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { CancelSubscription } from '@components/Modal/Subscriptions/CancelSubscription';
import { GracePeriod } from '../SubscriptionStatus/GracePeriod';
import { Cancelled } from '../SubscriptionStatus/Cancelled';
import { Suspended } from '../SubscriptionStatus/Suspended';
import { Active } from '../SubscriptionStatus/Active';
import { Trial } from '../SubscriptionStatus/Trial';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';

export const SubscriptionDetails = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [showCancellationModal, setShowCancellationModal] =
    useState<boolean>(false);
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] =
    useState<number>(0);

  const BreadCrumbsProps = {
    steps: AppSettingsSubscriptionSteps,
    activeItem: 'Subscription',
    type: OrderProcessingMasterData.APP_SETTINGS,
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  useEffect(() => {
    console.log('currentSubscriptionStatus', currentSubscriptionStatus);
  }, [currentSubscriptionStatus]);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      // Set the current update subscription status
      setCurrentSubscriptionStatus(
        response.data.data.subscription.subscriptionStatus,
      );
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={10} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />

        <div className="flex items-center justify-between mt-4 mb-4">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-[#1C1B1F] hover:text-primary text-lg mr-2 focus:outline-none"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <h2 className="text-2xl font-semibold ml-2">Subscription</h2>
          </div>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/subscription/details/billing')}
              className="px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              View Billing Details
            </button>
          </div>
        </div>

        {/* Main content */}
        {showCancellationModal && (
          <CancelSubscription onClose={() => setShowCancellationModal(false)} />
        )}

        {/* Active Plan */}
        {currentSubscriptionStatus == butcherSubscriptionsStatus.ACTIVE && (
          <Active />
        )}

        {/* Trial Period */}
        {currentSubscriptionStatus == butcherSubscriptionsStatus.TRIAL && (
          <Trial />
        )}

        {/* Suspended Subscription */}
        {currentSubscriptionStatus == butcherSubscriptionsStatus.SUSPENDED && (
          <Suspended />
        )}

        {/* Cancelled Subscription */}
        {currentSubscriptionStatus == butcherSubscriptionsStatus.CANCELLED && (
          <Cancelled />
        )}

        {/* Grace Period */}
        {currentSubscriptionStatus ==
          butcherSubscriptionsStatus.GRACE_PERIOD && <GracePeriod />}

        {/* Common subscription detais */}
        {currentSubscriptionStatus != butcherSubscriptionsStatus.TRIAL && (
          <ButcherSubscriptionDetails />
        )}

        {/* Cancel Subscription */}
        {/* Only when the plan is active or in grace period */}
        {(currentSubscriptionStatus === butcherSubscriptionsStatus.ACTIVE ||
          currentSubscriptionStatus ===
            butcherSubscriptionsStatus.GRACE_PERIOD) && (
          <div className="bg-white border border-[#E8E8E8] rounded-xl px-5 py-4">
            <button
              type="button"
              onClick={() => setShowCancellationModal(true)}
              className="flex items-center justify-between w-full group"
            >
              <div className="flex items-center gap-2 text-red-500 font-semibold">
                {/* Trash icon */}
                <FontAwesomeIcon icon={faTrashCan} />
                Cancel Subscription
              </div>
              <FontAwesomeIcon icon={faAngleRight} className="text-[#1C1B1F]" />
            </button>

            <div className="flex items-start gap-2 mt-3 bg-[#F3F4F6] rounded-lg px-3 py-2.5">
              <span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 15H11V9H9V15ZM10.7125 6.7125C10.9042 6.52083 11 6.28333 11 6C11 5.71667 10.9042 5.47917 10.7125 5.2875C10.5208 5.09583 10.2833 5 10 5C9.71667 5 9.47917 5.09583 9.2875 5.2875C9.09583 5.47917 9 5.71667 9 6C9 6.28333 9.09583 6.52083 9.2875 6.7125C9.47917 6.90417 9.71667 7 10 7C10.2833 7 10.5208 6.90417 10.7125 6.7125ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
                    fill="#4B5563"
                  />
                </svg>
              </span>
              <p className="text-[#4B5563] text-sm leading-snug">
                If you cancel your subscription, your data will be retained for
                one year. You can reactivate your subscription at any time
                during this period. Any outstanding invoice amount will be
                charged to your credit card.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
